import React, { useState, useEffect, useRef, type CSSProperties } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { IconsType } from '../../../types/admin';

type NotificationType = 'work_claimed' | 'work_assigned' | 'work_reassigned' | 'mention';

interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  body: string;
  actorName: string;
  read: boolean;
  createdAt: string;
}

// Mock notification data
const mockNotifications: Notification[] = [
  {
    id: 'notif-001',
    type: 'work_claimed',
    title: 'Work item claimed',
    body: 'Operator Two claimed "Brown v. Taylor" from your backlog',
    actorName: 'Operator Two',
    read: false,
    createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'notif-002',
    type: 'work_assigned',
    title: 'New work assigned',
    body: 'Admin User assigned you "Garcia v. Martinez"',
    actorName: 'Admin User',
    read: true,
    createdAt: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'notif-003',
    type: 'work_reassigned',
    title: 'Work reassigned',
    body: 'Admin User reassigned "Motion Response" to Operator Three',
    actorName: 'Admin User',
    read: false,
    createdAt: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
  },
];

const formatRelativeTime = (dateString: string): string => {
  const now = Date.now();
  const date = new Date(dateString).getTime();
  const diff = now - date;

  const minutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
};

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (id: string) => void;
  brand: BrandConfig;
}

const NotificationItem: React.FC<NotificationItemProps> = ({ notification, onMarkAsRead, brand }) => {
  const typeIcons: Record<NotificationType, string> = {
    work_claimed: '📥',
    work_assigned: '📋',
    work_reassigned: '🔄',
    mention: '💬',
  };

  return (
    <div
      className={`p-3 border-b cursor-pointer transition-colors hover:bg-gray-50 ${!notification.read ? 'bg-blue-50/50' : ''}`}
      style={{ borderColor: brand.colors.border }}
      role="button"
      tabIndex={0}
      onClick={() => !notification.read && onMarkAsRead(notification.id)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (!notification.read) onMarkAsRead(notification.id); } }}
    >
      <div className="flex items-start gap-2">
        <span className="text-lg">{typeIcons[notification.type] || '📢'}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span
              className="font-medium text-sm truncate"
              style={{ color: notification.read ? brand.colors.textLight : brand.colors.text }}
            >
              {notification.title}
            </span>
            {!notification.read && (
              <span
                className="w-2 h-2 rounded-full shrink-0"
                style={{ backgroundColor: brand.colors.primary }}
              />
            )}
          </div>
          <p
            className="text-xs mt-0.5 line-clamp-2"
            style={{ color: brand.colors.textLight }}
          >
            {notification.body}
          </p>
          <span className="text-xs mt-1 block" style={{ color: brand.colors.textMuted }}>
            {formatRelativeTime(notification.createdAt)}
          </span>
        </div>
      </div>
    </div>
  );
};

const FallbackBellIcon = ({ style }: { style?: CSSProperties }) => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={style}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
    />
  </svg>
);

interface NotificationBellProps {
  brand: BrandConfig;
  Icons: IconsType;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ brand, Icons }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>(mockNotifications);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const unreadCount = notifications.filter(n => !n.read).length;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleMarkAsRead = (notificationId: string): void => {
    setNotifications(prev =>
      prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
    );
  };

  const handleMarkAllAsRead = (): void => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const BellIcon = Icons.Bell || FallbackBellIcon;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg transition-colors hover:bg-gray-100"
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
      >
        <BellIcon style={{ color: brand.colors.textLight }} />

        {/* Unread badge */}
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 w-5 h-5 flex items-center justify-center text-xs font-bold rounded-full"
            style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          className="absolute right-0 mt-2 w-80 rounded-xl shadow-lg overflow-hidden z-50"
          style={{ backgroundColor: 'white', border: `1px solid ${brand.colors.border}` }}
        >
          {/* Header */}
          <div
            className="px-4 py-3 flex items-center justify-between border-b"
            style={{ borderColor: brand.colors.border }}
          >
            <h3 className="font-semibold" style={{ color: brand.colors.secondary }}>
              Notifications
            </h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="text-xs hover:underline"
                style={{ color: brand.colors.link }}
              >
                Mark all as read
              </button>
            )}
          </div>

          {/* Notification list */}
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="py-8 text-center">
                <span className="text-3xl mb-2 block">🔔</span>
                <p className="text-sm" style={{ color: brand.colors.textLight }}>
                  No notifications yet
                </p>
              </div>
            ) : (
              notifications.map(notification => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onMarkAsRead={handleMarkAsRead}
                  brand={brand}
                />
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div
              className="px-4 py-2 text-center border-t"
              style={{ borderColor: brand.colors.border }}
            >
              <button
                className="text-sm hover:underline"
                style={{ color: brand.colors.link }}
              >
                View all notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
